import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Share2, ArrowLeftCircle, CalendarDays, Building2, Stethoscope, UserRound, FileText, Pill, Lock, ClipboardList } from 'lucide-react';
import CommonLoader from '../components/CommonLoader';
import WhoLabel from '../components/common/WhoLabel';
import { tidyName } from '../utils/tidyName';
import { formatDay } from '../components/health/healthFormat';
import { formatDose } from '../components/patientTabs/prescriptionHelpers';
import { formatLooseValue, hhmm } from '../utils/checkSets';
import { fetchReceivedShares, fetchReceivedShare, closeSharedVisit, clearUnseenShares } from '../redux/appointmentShareSlice';

// "SHARED WITH ME" (doc/one-patient-row.md §9, Phase 4).
//
// A patient shares ONE past visit — at whichever hospital it happened — with a
// doctor who has treated them. This page is the receiving end, and it is the
// platform's only cross-tenant clinical read: everything on it was written at
// another hospital, by another doctor, and reached this screen because the
// patient said so.
//
// SO IT IS READ-ONLY, AND SAYS WHY. Nothing here can be edited, replied to or
// copied into this hospital's notes by a button, and the note at the top names
// the consent that put it on screen. The snapshot carries the doctor's own
// notes and the prescription — never the recording, the transcript or the AI
// summary: agreeing to show one consultation is not agreeing to hand over a
// recording of it. Since check sets (Round A) it also carries that visit's
// live check READINGS (`readings`, oldest first) — labels as they were when
// taken, and no reader's name.
//
// A REVOKED SHARE IS A 403, AND NOT AN ERROR. The share row is re-read on
// every request, so a patient who changes their mind locks the doctor out on
// the next click rather than the next login — and the page says "This share
// was withdrawn." instead of showing a failure the doctor would report as a
// bug.
//
// IT IS LIVE (visit sharing v2). VisitShareSocketBridge, mounted app-wide,
// prepends a newly shared visit and drops a revoked one in the same slice
// this page reads, so the list moves without a reload.

/** "1-0-½-1 AF" from the four counters, or the T-mode instruction as typed. */
const scheduleOf = (item) => {
    if (!item) return '';
    if (item.entryMode === 'T') return String(item.notes || '').trim();
    const counts = [item.morningCount, item.afternoonCount, item.eveningCount, item.nightCount].map(formatDose);
    return `${counts.join('-')} ${item.foodTiming || ''}`.trim();
};

function SharedVisits() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        received, receivedLoading, receivedError,
        snapshot, snapshotShareId, snapshotLoading, snapshotError,
    } = useSelector((state) => state.appointmentShare);

    useEffect(() => {
        dispatch(fetchReceivedShares());
        // Opening the page is "seeing" what arrived: the sidebar badge clears.
        // New shares while it is open are prepended live by
        // VisitShareSocketBridge and never counted (it knows the route).
        dispatch(clearUnseenShares());
    }, [dispatch]);

    const openShare = (shareId) => dispatch(fetchReceivedShare({ shareId }));

    /* ---------------- the one shared visit, opened ---------------- */
    if (snapshotShareId && (snapshotLoading || snapshot || snapshotError)) {
        const notes = snapshot?.visitNotes || {};
        const items = snapshot?.prescription?.items || [];
        const readings = Array.isArray(snapshot?.readings) ? snapshot.readings : [];
        const roleLabel = (role) => t(`checkSets.role_${role === 'doctor' || role === 'patient' ? role : 'staff'}`);
        const noteBlocks = [
            { key: 'written', label: t('sharedVisits.notes_written'), value: notes.written },
            { key: 'consultation', label: t('sharedVisits.notes_consultation'), value: notes.consultation },
            { key: 'summary', label: t('sharedVisits.notes_summary'), value: notes.summary },
            // 30 Sep 2026: the sections of the doctor's own note form, which
            // is where a visit's notes are written today. The server sends
            // them as [{ label, value }] in the doctor's order; a section
            // whose heading is no longer known gets a plain "Note".
            ...(Array.isArray(notes.custom) ? notes.custom : []).map((n, i) => ({
                key: `custom-${i}`,
                label: n?.label || t('sharedVisits.notes_custom', 'Note'),
                value: n?.value,
            })),
        ].filter((b) => b.value);

        return (
            <div data-testid="shared-visit-detail" className="mx-auto rounded-2xl border border-gray-100 bg-white p-4 shadow-xl sm:p-6">
                <div className="mb-4 flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="shared-visit-back"
                        onClick={() => dispatch(closeSharedVisit())}
                        className="cursor-pointer rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] p-2 text-white shadow-lg transition-all duration-300 hover:shadow-xl"
                        aria-label={t('sharedVisits.back')}
                    >
                        <ArrowLeftCircle className="h-5 w-5" aria-hidden="true" />
                    </button>
                    <h1 className="ml-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-2xl font-bold text-transparent">
                        {t('sharedVisits.detail_heading')}
                    </h1>
                </div>

                {snapshotLoading && (
                    <div data-testid="shared-visit-loading" className="flex flex-col items-center py-12">
                        <CommonLoader />
                        <span className="text-sm text-[#4ABDBD]">{t('sharedVisits.loading')}</span>
                    </div>
                )}

                {!snapshotLoading && snapshotError && (
                    <div data-testid="shared-visit-withdrawn" className="mx-auto max-w-xl rounded-2xl border border-[#C7D6D5] bg-[#F5FAFA] p-8 text-center">
                        <Lock className="mx-auto mb-3 h-10 w-10 text-[#9DB3B1]" aria-hidden="true" />
                        <p className="text-sm text-gray-600">
                            {snapshotError.status === 403 ? t('sharedVisits.withdrawn') : t('sharedVisits.load_failed')}
                        </p>
                    </div>
                )}

                {!snapshotLoading && snapshot && (
                    <div className="flex flex-col gap-4">
                        <p data-testid="shared-visit-read-only-note" className="rounded-xl border border-[#4ABDBD]/30 bg-[#4ABDBD]/10 p-3 text-xs font-medium text-[#2D8A8A]">
                            {t('sharedVisits.read_only_note')}
                        </p>

                        <div className="grid grid-cols-1 gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 sm:grid-cols-2">
                            <p className="flex items-center gap-2 text-sm text-gray-700">
                                <UserRound className="h-4 w-4 text-[#2D8A8A]" aria-hidden="true" />
                                <span className="text-gray-400">{t('common.patient_label', 'Patient')}:</span>{' '}
                                <strong data-testid="shared-visit-patient" className="font-semibold text-gray-800">{tidyName(snapshot.patient?.name)}</strong>
                            </p>
                            <p className="flex items-center gap-2 text-sm text-gray-700">
                                <CalendarDays className="h-4 w-4 text-[#2D8A8A]" aria-hidden="true" />
                                <span className="font-semibold">{t('sharedVisits.visit_date')}:</span>
                                <span data-testid="shared-visit-date">{formatDay(snapshot.appointment?.date) || t('sharedVisits.not_recorded')}</span>
                            </p>
                            <p className="flex items-center gap-2 text-sm text-gray-700">
                                <Stethoscope className="h-4 w-4 text-[#2D8A8A]" aria-hidden="true" />
                                <span className="text-gray-400">{t('common.doctor_label', 'Doctor')}:</span>{' '}
                                {/* "Dr Meena · Chitra Clinic, Chennai" — who
                                    treated them, and where. */}
                                <span data-testid="shared-visit-doctor">
                                    <strong className="font-semibold text-gray-800">{tidyName(snapshot.doctor?.name)}</strong>
                                    {snapshot.doctor?.specialty ? ` · ${snapshot.doctor.specialty}` : ''}
                                </span>
                            </p>
                            <p className="flex items-center gap-2 text-sm text-gray-700">
                                <Building2 className="h-4 w-4 text-[#2D8A8A]" aria-hidden="true" />
                                <span className="font-semibold">{t('sharedVisits.hospital')}:</span>
                                <span data-testid="shared-visit-hospital">
                                    {snapshot.hospital?.name}
                                    {snapshot.hospital?.city ? `, ${snapshot.hospital.city}` : ''}
                                </span>
                            </p>
                            {snapshot.appointment?.reason && (
                                <p className="text-sm text-gray-700 sm:col-span-2">
                                    <span className="font-semibold">{t('sharedVisits.reason')}:</span>{' '}
                                    <span data-testid="shared-visit-reason">{snapshot.appointment.reason}</span>
                                </p>
                            )}
                            {snapshot.appointment?.status && (
                                <p className="text-sm text-gray-700 sm:col-span-2">
                                    <span className="font-semibold">{t('sharedVisits.status')}:</span>{' '}
                                    <span data-testid="shared-visit-status">{snapshot.appointment.status}</span>
                                </p>
                            )}
                        </div>

                        <section data-testid="shared-visit-notes">
                            <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-[#4ABDBD]">
                                <FileText className="h-5 w-5" aria-hidden="true" />
                                {t('sharedVisits.visit_notes')}
                            </h2>
                            {noteBlocks.length === 0 ? (
                                <p data-testid="shared-visit-no-notes" className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                                    {t('sharedVisits.no_notes')}
                                </p>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    {noteBlocks.map((b) => (
                                        <div key={b.key} data-testid={`shared-visit-note-${b.key}`}>
                                            <h3 className="text-xs font-semibold text-gray-500">{b.label}</h3>
                                            <p className="mt-1 whitespace-pre-wrap rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">{b.value}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>

                        <section data-testid="shared-visit-readings">
                            <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-[#4ABDBD]">
                                <ClipboardList className="h-5 w-5" aria-hidden="true" />
                                {t('sharedVisits.readings')}
                            </h2>
                            {readings.length === 0 ? (
                                <p data-testid="shared-visit-no-readings" className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                                    {t('sharedVisits.no_readings')}
                                </p>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    {readings.map((r, i) => (
                                        <div key={`${r.setName}-${r.recordedAt}-${i}`} data-testid={`shared-visit-reading-${i}`} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                                                <h3 className="text-sm font-semibold text-gray-800">{r.setName}</h3>
                                                <span className="text-[11px] text-gray-500">
                                                    {roleLabel(r.recordedRole)} · {formatDay(r.recordedAt)} {hhmm(r.recordedAt)}
                                                </span>
                                            </div>
                                            <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                                                {(r.values || []).map((v, j) => (
                                                    <div key={`${v.label}-${j}`} className="flex min-w-0 items-baseline gap-2 text-sm">
                                                        <dt className="shrink-0 text-gray-500">{v.label}</dt>
                                                        <dd className="min-w-0 break-words font-semibold text-gray-800">{formatLooseValue(v.value, v.unit, t)}</dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>

                        <section data-testid="shared-visit-prescription">
                            <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-[#4ABDBD]">
                                <Pill className="h-5 w-5" aria-hidden="true" />
                                {t('sharedVisits.prescription')}
                            </h2>
                            {items.length === 0 ? (
                                <p data-testid="shared-visit-no-prescription" className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                                    {t('sharedVisits.no_prescription')}
                                </p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse overflow-hidden rounded-xl text-sm">
                                        <thead className="bg-[#4ABDBD]/10 text-left text-xs text-[#2D8A8A]">
                                            <tr>
                                                <th className="p-2">{t('sharedVisits.medicine')}</th>
                                                <th className="p-2">{t('sharedVisits.dose')}</th>
                                                <th className="p-2">{t('sharedVisits.schedule')}</th>
                                                <th className="p-2">{t('sharedVisits.duration')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((item) => (
                                                <tr key={item.prescriptionId} data-testid={`shared-visit-medicine-${item.prescriptionId}`} className="border-t border-gray-100">
                                                    <td className="p-2 font-medium text-gray-800">
                                                        {item.medicineName}
                                                        {item.brand ? <span className="ml-1 text-xs text-gray-400">({item.brand})</span> : null}
                                                    </td>
                                                    <td className="p-2 text-gray-600">{item.dose || '—'}</td>
                                                    <td className="p-2 text-gray-600">{scheduleOf(item) || '—'}</td>
                                                    <td className="p-2 text-gray-600">
                                                        {item.durationDays ? t('sharedVisits.duration_days', { days: item.durationDays }) : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </div>
        );
    }

    /* ---------------- the list ---------------- */
    return (
        <div data-testid="shared-visits-page" className="mx-auto rounded-2xl border border-gray-100 bg-white p-4 shadow-xl sm:p-6">
            <div className="mb-1 flex items-center gap-2">
                <Share2 className="h-6 w-6 text-[#2D8A8A]" aria-hidden="true" />
                <h1 className="bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-2xl font-bold text-transparent sm:text-3xl">
                    {t('sharedVisits.heading')}
                </h1>
            </div>
            <p className="mb-4 text-sm text-gray-500">{t('sharedVisits.lead')}</p>

            {receivedLoading && (
                <div data-testid="shared-visits-loading" className="flex flex-col items-center py-12">
                    <CommonLoader />
                    <span className="text-sm text-[#4ABDBD]">{t('sharedVisits.loading')}</span>
                </div>
            )}

            {!receivedLoading && receivedError && (
                <div data-testid="shared-visits-error" className="mx-auto max-w-xl rounded-2xl border border-red-100 bg-red-50 p-8 text-center">
                    <p className="text-sm text-red-700">{t('sharedVisits.load_failed')}</p>
                    <button
                        type="button"
                        data-testid="shared-visits-retry"
                        onClick={() => dispatch(fetchReceivedShares())}
                        className="mt-3 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl"
                    >
                        {t('sharedVisits.retry')}
                    </button>
                </div>
            )}

            {!receivedLoading && !receivedError && received.length === 0 && (
                <div data-testid="shared-visits-empty" className="mx-auto max-w-xl rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                    <Share2 className="mx-auto mb-3 h-10 w-10 text-[#9DB3B1]" aria-hidden="true" />
                    <h2 className="text-base font-bold text-gray-700">{t('sharedVisits.empty_title')}</h2>
                    <p className="mt-2 text-sm text-gray-500">{t('sharedVisits.empty_body')}</p>
                </div>
            )}

            {!receivedLoading && !receivedError && received.length > 0 && (
                <ul data-testid="shared-visits-list" className="flex flex-col gap-3">
                    {received.map((share) => (
                        <li
                            key={share.shareId}
                            data-testid={`shared-visit-row-${share.shareId}`}
                            className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-colors hover:border-[#4ABDBD]/40"
                        >
                            <div className="min-w-0">
                                <p className="text-sm text-gray-800">
                                    <WhoLabel role="patient" name={share.patientName} tidy testId={`shared-visit-row-patient-${share.shareId}`} />
                                </p>
                                <p className="mt-0.5 text-xs text-gray-500">
                                    {formatDay(share.appointmentDate)}
                                    {share.doctorName ? <>{' · '}<WhoLabel role="doctor" name={share.doctorName} tidy nameClassName="font-semibold text-gray-600" testId={`shared-visit-row-doctor-${share.shareId}`} /></> : ''}
                                    {share.hospitalName ? ` · ${share.hospitalName}` : ''}
                                </p>
                                <p className="mt-0.5 text-[11px] text-gray-400">
                                    {t('sharedVisits.shared_on', { date: formatDay(share.grantedAt) })}
                                </p>
                            </div>
                            <button
                                type="button"
                                data-testid={`shared-visit-open-${share.shareId}`}
                                onClick={() => openShare(share.shareId)}
                                className="rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl"
                            >
                                {t('sharedVisits.open')}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default SharedVisits;
